import { JobEntity, useSalesTaxProducts } from '@commenda/api';
import { ICommendaProduct } from '@commenda/api/new-sales-tax/products/types';
import { LoadingIndicator, useToggle } from '@commenda/kakashi';
import { TSetNotification } from '@commenda/types/src/random';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSalesTaxRootFiConnectFlow } from '../common/useSalesTaxRootFiConnectFlow';
import { SALES_TAX_TABLE_IDS } from '../common/constants';
import { FilterChangeEvent, ProductFilterState } from '../common/filters/types';
import { isFilterValueEmpty } from '../common/filters/utils';
import DataSyncBanner from '../common/integrationDataSync/DataSyncBanner';
import { getIntegrationSyncState } from '../common/integrationDataSync/utils';
import { useIndirectTaxJobWatcher } from '../common/jobs';
import { calculatePaginationRangeForCursorBasedPagination } from '../common/usePaginationRange';
import { downloadSalesTaxProductsCSVTemplate } from '../common/utils/salesTaxCsvTemplate';
import {
  loadFiltersFromSessionStorage,
  loadSearchTermFromSessionStorage,
  saveFiltersToSessionStorage,
  saveSearchTermToSessionStorage,
} from '../common/utils/sessionStorage';
import { useIntegrationSyncAndValidationProgressStatus } from '../data-review/useIntegrationSyncAndValidationProgressStatus';
import { formatNumberWithCommas } from '../utils';
import SalesTaxAddNewProductModal from './modal/add-new-product';
import SalesTaxImportProductsModal from './modal/products-upload-modal';
import ProductFilters from './ProductFilters/ProductFilter';
import ProductsTableSection from './products-table-section';
import ProductTabEmptyState from './ProductTabEmptyState';
import ProductTabHeader from './ProductTabHeader';
import TaxCodeReferenceLinkBanner from './TaxCodeReferenceLinkBanner';

interface SalesTaxProductsTabProps {
  setNotification: TSetNotification;
  corporationId: number;
  companyId: number;
}

const SalesTaxProductsTab = ({
  setNotification,
  corporationId,
  companyId,
}: SalesTaxProductsTabProps) => {
  const [currentCursor, setCurrentCursor] = useState<string | null>(null);
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);
  const [pageSize] = useState(100);
  const FIRST_PAGE_CURSOR = 'FIRST_PAGE_CURSOR';
  const [hasInitialData, setHasInitialData] = useState(false);
  const [isAddingNewProduct, setIsAddingNewProduct] = useState(false);

  const [filters, _setFilters] = useState<ProductFilterState>({
    corporationId,
  });

  const setFilters = useCallback(
    (
      updater:
        | ProductFilterState
        | ((prev: ProductFilterState) => ProductFilterState)
    ) => {
      _setFilters((prev) => {
        const next =
          typeof updater === 'function'
            ? (updater as (p: ProductFilterState) => ProductFilterState)(prev)
            : updater;
        saveFiltersToSessionStorage(
          corporationId,
          SALES_TAX_TABLE_IDS.PRODUCTS,
          next
        );
        return next;
      });
    },
    [corporationId]
  );

  const [searchTerm, _setSearchTerm] = useState<string>('');

  const setSearchTerm = useCallback(
    (updater: string | ((prev: string) => string)) => {
      _setSearchTerm((prev) => {
        const next =
          typeof updater === 'function'
            ? (updater as (p: string) => string)(prev)
            : updater;
        saveSearchTermToSessionStorage(
          corporationId,
          SALES_TAX_TABLE_IDS.PRODUCTS,
          next
        );
        return next;
      });
    },
    [corporationId]
  );

  const [productsListData, setProductsListData] = useState<ICommendaProduct[]>(
    []
  );

  const addProductModal = useToggle();
  const importProductsModal = useToggle();

  const {
    integrations,
    handleConnectIntegrationClick,
    connectRootFiIntegrationModal,
  } = useSalesTaxRootFiConnectFlow({ corporationId, setNotification });

  const {
    data: productsData,
    mutate: mutateProducts,
    error: productsError,
    isLoading: isLoadingProducts,
  } = useSalesTaxProducts({
    filters,
    search_term: searchTerm,
    cursor: currentCursor === FIRST_PAGE_CURSOR ? null : currentCursor,
    limit: pageSize,
  });

  const { isIntegrationSyncRecordDataProcessing } =
    useIntegrationSyncAndValidationProgressStatus(corporationId);

  const {
    isJobInProgress,
    refreshJobs,
    watchEntityWithRefresh,
    getJobsByEntity,
  } = useIndirectTaxJobWatcher({
    corporation_id: corporationId,
    entity: JobEntity.PRODUCT,
  });

  const hasActiveFilters = useMemo(() => {
    return (
      Object.keys(filters || {}).some(
        (key) => filters[key] && key !== 'corporationId'
      ) || searchTerm !== ''
    );
  }, [filters]);

  const loadProductsFiltersFromSessionStorage = useCallback(() => {
    const savedFilters: ProductFilterState = loadFiltersFromSessionStorage(
      corporationId,
      SALES_TAX_TABLE_IDS.PRODUCTS
    );
    return {
      corporationId,
      ...savedFilters,
    };
  }, [corporationId]);

  const loadProductsSearchTermFromSessionStorage = useCallback(() => {
    return (
      loadSearchTermFromSessionStorage(
        corporationId,
        SALES_TAX_TABLE_IDS.PRODUCTS
      ) || ''
    );
  }, [corporationId]);

  useEffect(() => {
    setFilters(loadProductsFiltersFromSessionStorage());
    setSearchTerm(loadProductsSearchTermFromSessionStorage());
  }, [
    loadProductsFiltersFromSessionStorage,
    loadProductsSearchTermFromSessionStorage,
  ]);

  useEffect(() => {
    if (productsData && !isLoadingProducts) {
      // Only update if the data has actually changed
      if (
        JSON.stringify(productsData.products) !==
        JSON.stringify(productsListData)
      ) {
        setProductsListData(productsData.products);
      }
    }
  }, [productsData, isLoadingProducts]);

  useEffect(() => {
    if (productsData?.products?.length > 0) {
      setHasInitialData(true);
    }
  }, [productsData?.products]);

  const handleUploadSuccess = async () => {
    resetCursor();
    refreshJobs();
    await mutateProducts();
  };

  const debouncedSearch = useCallback(
    debounce((searchValue: string) => {
      resetCursor();
      setSearchTerm(searchValue);
    }, 500),
    []
  );

  const handleSearch = (searchValue: string) => {
    debouncedSearch(searchValue);
  };

  const handleFilterChange = (event: FilterChangeEvent) => {
    resetCursor();
    if (!isFilterValueEmpty(event.value)) {
      setFilters((prev) => ({ ...prev, [event.filterId]: event.value }));
    } else {
      setFilters((prev) => {
        const newFilters = { ...prev };
        delete newFilters[event.filterId];
        return newFilters;
      });
    }
  };

  const onFilterClear = (filterId: string) => {
    resetCursor();
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[filterId];
      return newFilters;
    });
  };

  const onClearAll = () => {
    resetCursor();
    setFilters({ corporationId });
  };

  const handlePageChange = (newCursor: string | null) => {
    if (newCursor === currentCursor) {
      // Going back
      const previousCursor = cursorHistory[cursorHistory.length - 1];
      setCursorHistory((prev) => prev.slice(0, -1));
      setCurrentCursor(previousCursor || null);
    } else {
      // Going forward
      setCursorHistory((prev) => [...prev, currentCursor || FIRST_PAGE_CURSOR]);
      setCurrentCursor(newCursor);
    }
  };

  const resetCursor = () => {
    setCursorHistory([]);
    setCurrentCursor(null);
  };

  const handleDownloadCSVTemplate = () => {
    downloadSalesTaxProductsCSVTemplate();
    setNotification({
      show: true,
      valid: true,
      title: 'Successfully downloaded Sales Tax Products CSV Template',
      message: 'CSV template downloaded successfully',
    });
  };

  const isIntegrationSyncInProgress = getIntegrationSyncState(integrations);

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    // Start watching only if the entity is processing
    if (isJobInProgress(JobEntity.PRODUCT)) {
      cleanup = watchEntityWithRefresh(JobEntity.PRODUCT, () => {
        mutateProducts();
      });
    }

    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [isJobInProgress, watchEntityWithRefresh, mutateProducts]);

  useEffect(() => {
    resetCursor();
  }, [filters]);

  const { startIndexOfPage, endIndexOfPage, totalItemsForPage } =
    calculatePaginationRangeForCursorBasedPagination({
      cursorHistoryLength: cursorHistory.length,
      pageSize,
      currentItemsCount: productsListData.length,
      totalItems: productsData?.totalProducts || 0,
    });

  if (
    !productsData ||
    productsError ||
    (isLoadingProducts && !hasInitialData)
  ) {
    return (
      <main className="flex items-center justify-center min-h-screen">
        <LoadingIndicator />
      </main>
    );
  }

  return (
    <>
      {connectRootFiIntegrationModal}
      <SalesTaxAddNewProductModal
        isProductsModalShown={addProductModal.visible}
        setIsProductsModalShown={addProductModal.hide}
        corporationId={corporationId}
        setNotification={setNotification}
        mutate={mutateProducts}
        setIsAddingNewProduct={setIsAddingNewProduct}
      />
      <SalesTaxImportProductsModal
        isProductsModalShown={importProductsModal.visible}
        setIsProductsModalShown={importProductsModal.hide}
        setNotification={setNotification}
        mutate={mutateProducts}
        corporationId={corporationId}
        onSuccess={handleUploadSuccess}
        companyId={companyId}
      />

      {!hasInitialData &&
      !hasActiveFilters &&
      !productsData?.products?.length &&
      !getJobsByEntity(JobEntity.PRODUCT).length &&
      !(isJobInProgress(JobEntity.PRODUCT) || isIntegrationSyncInProgress) ? (
        <ProductTabEmptyState
          onAddProduct={addProductModal.show}
          onImportProducts={importProductsModal.show}
          onConnectIntegration={handleConnectIntegrationClick}
          onDownloadTemplate={handleDownloadCSVTemplate}
        />
      ) : (
        <>
          {(isIntegrationSyncInProgress ||
            isJobInProgress(JobEntity.PRODUCT) ||
            isIntegrationSyncRecordDataProcessing()) && (
            <DataSyncBanner
              corporationId={corporationId}
              entity={JobEntity.PRODUCT}
              isSyncInProgress={isIntegrationSyncRecordDataProcessing()}
            />
          )}

          <main className="flex flex-col">
            <TaxCodeReferenceLinkBanner />

            <ProductTabHeader
              onAddProduct={addProductModal.show}
              onImportProducts={importProductsModal.show}
              onConnectIntegration={handleConnectIntegrationClick}
              corporationId={corporationId}
            />
            <ProductFilters
              filterValues={filters}
              corporationId={corporationId}
              onFilterChange={handleFilterChange}
              onFilterClear={onFilterClear}
              onClearAll={onClearAll}
            />
            {totalItemsForPage > 0 ? (
              <div className="txt-small-regular text-text-quaternary mt-4 mb-4 px-2">
                Showing {startIndexOfPage}-{endIndexOfPage} of{' '}
                {formatNumberWithCommas(totalItemsForPage)}{' '}
                {totalItemsForPage === 1 ? 'product' : 'products'}
              </div>
            ) : (
              <div className="txt-small-regular text-text-quaternary mt-4 mb-4 px-2">
                Showing 0-0 of 0 products
              </div>
            )}
            <ProductsTableSection
              data={productsListData}
              filters={filters}
              searchTerm={searchTerm}
              onSearch={handleSearch}
              corporationId={corporationId}
              pageSize={pageSize}
              totalProducts={productsData?.totalProducts || 0}
              nextCursor={productsData?.nextCursor}
              currentCursor={currentCursor}
              onPageChange={handlePageChange}
              mutate={mutateProducts}
              clearFilters={onClearAll}
              setNotification={setNotification}
              resetCursor={resetCursor}
            />
          </main>
        </>
      )}
    </>
  );
};

export default SalesTaxProductsTab;
